import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue, update } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  listAll,
  getDownloadURL,
} from "firebase/storage";
import "./Transporters.css";
import {
  FiEye,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiFileText,
  FiTruck,
  FiNavigation,
  FiHash,
  FiTag,
} from "react-icons/fi";

const Transporters = () => {
  const [transporters, setTransporters] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);

  useEffect(() => {
    const db = getDatabase();
    const transportersRef = ref(db, "transporter-profile");
    const clientsRef = ref(db, "profileDashboard");

    onValue(clientsRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setClients([]);
        return;
      }

      const clientList = Object.entries(data).map(([id, item]) => ({
        id,
        name:
          item.company_name ||
          item.companyName ||
          item.clientName ||
          item.contactPerson ||
          item.name ||
          item.username ||
          id,
      }));

      setClients(clientList);
    });

    const unsubscribe = onValue(transportersRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setTransporters([]);
        return;
      }

      const loadTransporters = async () => {
        const storage = getStorage();

        const transporterList = await Promise.all(
          Object.entries(data).map(async ([id, item]) => {
            let documentUrls = [];

            try {
              const folderRef = storageRef(
                storage,
                `transport-logi-docs/${id}`,
              );

              const files = await listAll(folderRef);

              if (files.items.length > 0) {
                documentUrls = await Promise.all(
                  files.items.map(async (file) => ({
                    name: file.name,
                    url: await getDownloadURL(file),
                  })),
                );
              }
            } catch (e) {
              documentUrls = [];
            }

            const isOwnerDriver = item.type === "OWNER_DRIVER";

            const displayName = item.company || item.contactPerson || "";

            return {
              id,
              company: item.company || "",
              displayName,
              type: item.type || "",
              contactPerson: item.contactPerson || "",
              email: item.email || "",
              phone: item.phone || "",
              gst: item.gst || "",
              state: item.state || "",
              docs: documentUrls,
              routes: item.routes || null,
              trucks: item.trucks || null,
              dispatch: item.dispatch || null,
              assignedClients: item.assignedClients || null,
              drivers: item.drivers || null,
            };
          }),
        );

        setTransporters(transporterList);

        if (selectedTransporter) {
          const updatedTransporter = transporterList.find(
            (t) => t.id === selectedTransporter.id,
          );

          if (updatedTransporter) {
            setSelectedTransporter(updatedTransporter);
          }
        }

        if (!selectedTransporter && transporterList.length > 0) {
          setSelectedTransporter(transporterList[0]);
        }
      };

      loadTransporters();
    });

    return () => unsubscribe();
  }, []);

  const assignClient = async () => {
    if (!selectedTransporter || !selectedClient) {
      alert("Please select a client");
      return;
    }

    try {
      const db = getDatabase();

      await update(ref(db, `transporter-profile/${selectedTransporter.id}`), {
        [`assignedClients/${selectedClient}`]: true,
      });

      setSelectedClient("");
      alert("Client assigned successfully");
    } catch (error) {
      console.error(error);
      alert("Failed to assign client");
    }
  };

  // Group transporters by type, handling various string formats
  const transportersList = transporters.filter((t) => {
    const type = String(t.type || "")
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
    return type === "TRANSPORTER";
  });

  const ownerDriversList = transporters.filter((t) => {
    const type = String(t.type || "")
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");

    return (
      type === "OWNER_DRIVER" || type === "OWNERDRIVER" || type === "OWNER"
    );
  });

  const assignedDrivers = selectedTransporter?.drivers
    ? Object.keys(selectedTransporter.drivers)
        .map((driverId) => transporters.find((t) => t.id === driverId))
        .filter(Boolean)
    : [];

  return (
    <div className="transporters-page">
      <div className="transporters-sidebar">
        <div className="transporters-title">Transporters</div>

        <div style={{ padding: "0 10px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            Transporters
          </div>

          {transportersList.map((transporter) => (
            <div
              key={transporter.id}
              className={`transporter-list-item ${selectedTransporter?.id === transporter.id ? "active" : ""}`}
              onClick={() => setSelectedTransporter(transporter)}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <h4>{transporter.displayName}</h4>
                <span className="transporter-type-badge">
                  {transporter.type}
                </span>
              </div>
              {transporter.contactPerson ? (
                <p>{transporter.contactPerson}</p>
              ) : null}
            </div>
          ))}

          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              marginTop: "18px",
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            Owner Drivers
          </div>

          {ownerDriversList.map((transporter) => (
            <div
              key={transporter.id}
              className={`transporter-list-item ${selectedTransporter?.id === transporter.id ? "active" : ""}`}
              onClick={() => setSelectedTransporter(transporter)}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <h4>{transporter.displayName}</h4>
                <span className="transporter-type-badge">
                  {transporter.type}
                </span>
              </div>
              {transporter.contactPerson ? (
                <p>{transporter.contactPerson}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="transporter-details-panel">
        {selectedTransporter ? (
          <div className="transporter-details-card">
            <div className="transporter-header-row">
              <h2>{selectedTransporter.displayName}</h2>
              <span className="transporter-type-badge transporter-type-large">
                {selectedTransporter.type}
              </span>
            </div>

            {/* Assign Client section moved up here */}
            <div className="details-section">
              <h3>
                <FiUser
                  style={{ marginRight: "8px", verticalAlign: "middle" }}
                />
                Assign Client
              </h3>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  style={{
                    minWidth: "260px",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                  }}
                >
                  <option value="">Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!selectedTransporter || !selectedClient) {
                      alert("Please select a client");
                      return;
                    }
                    setShowAssignConfirm(true);
                  }}
                  style={{
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Assign Client
                </button>
              </div>
            </div>

            {/* Assigned Clients section */}
            {selectedTransporter.assignedClients &&
            Object.keys(selectedTransporter.assignedClients).length > 0 ? (
              <div className="details-section">
                <h3>
                  <FiUser
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Assigned Clients
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  {Object.keys(selectedTransporter.assignedClients).map(
                    (clientId) => {
                      const client = clients.find((c) => c.id === clientId);
                      return (
                        <div
                          key={clientId}
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            background: "#f9fafb",
                            marginBottom: "20px",
                          }}
                        >
                          {client?.name || clientId}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            ) : null}

            {assignedDrivers.length > 0 && (
              <div className="details-section">
                <h2>
                  <FiUser
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Assigned Drivers & their Trucks
                </h2>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {assignedDrivers.map((driver) => {
                    const linkedTrucks = selectedTransporter?.trucks
                      ? Object.values(selectedTransporter.trucks).filter(
                          (truck) =>
                            truck?.assignedDriverId === driver.id ||
                            truck?.assignedDriver?.id === driver.id,
                        )
                      : [];
                    return (
                      <div
                        key={driver.id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          padding: "12px",
                        }}
                      >
                        <div>
                          <strong>Name:</strong> {driver.displayName}
                        </div>
                        {driver.phone ? (
                          <div>
                            <strong>Phone:</strong> {driver.phone}
                          </div>
                        ) : null}
                        {driver.email ? (
                          <div>
                            <strong>Email:</strong> {driver.email}
                          </div>
                        ) : null}
                        {driver.state ? (
                          <div>
                            <strong>State:</strong> {driver.state}
                          </div>
                        ) : null}
                        <div>
                          <strong>Type:</strong> {driver.type}
                        </div>

                        {linkedTrucks.length > 0 ? (
                          <>
                            <div style={{ marginTop: "10px", fontWeight: 600 }}>
                              Assigned Trucks
                            </div>

                            {linkedTrucks.map((truck, idx) => (
                              <div
                                key={idx}
                                style={{
                                  marginTop: "6px",
                                  paddingTop: "6px",
                                  borderTop: "1px solid #eee",
                                  fontSize: "15px",
                                }}
                              >
                                {truck?.truckNumber ? (
                                  <div>
                                    <strong>Truck:</strong> {truck.truckNumber}
                                  </div>
                                ) : null}

                                {truck?.truckSize ? (
                                  <div>
                                    <strong>Size:</strong> {truck.truckSize}
                                  </div>
                                ) : null}

                                {truck?.from ? (
                                  <div>
                                    <strong>From:</strong> {truck.from}
                                  </div>
                                ) : null}

                                {truck?.to ? (
                                  <div>
                                    <strong>To:</strong> {truck.to}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h2 className="mt-3">Profile Details</h2>
            {/* Details grid */}
            <div className="details-grid">
              {selectedTransporter.contactPerson ? (
                <div>
                  <strong>
                    <FiUser
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    />
                    Contact Person:
                  </strong>{" "}
                  {selectedTransporter.contactPerson}
                </div>
              ) : null}
              {selectedTransporter.phone ? (
                <div>
                  <strong>
                    <FiPhone
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    />
                    Phone:
                  </strong>{" "}
                  {selectedTransporter.phone}
                </div>
              ) : null}
              {selectedTransporter.email ? (
                <div>
                  <strong>
                    <FiMail
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    />
                    Email:
                  </strong>{" "}
                  {selectedTransporter.email}
                </div>
              ) : null}
              {selectedTransporter.gst ? (
                <div>
                  <strong>
                    <FiFileText
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    />
                    GST:
                  </strong>{" "}
                  {selectedTransporter.gst}
                </div>
              ) : null}
              {selectedTransporter.state ? (
                <div>
                  <strong>
                    <FiMapPin
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    />
                    State:
                  </strong>{" "}
                  {selectedTransporter.state}
                </div>
              ) : null}
            </div>

            {/* Documents */}
            {selectedTransporter.docs && selectedTransporter.docs.length > 0 ? (
              <div className="details-section">
                <h3>
                  <FiFileText
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Transport Documents
                </h3>
                <div className="documents-row">
                  {selectedTransporter.docs.map((doc, index) => (
                    <a
                      key={index}
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="document-pill"
                    >
                      <span className="document-eye">
                        <FiEye />
                      </span>
                      <span className="document-name">{doc.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Routes */}
            {selectedTransporter.routes ? (
              <div className="details-section">
                <h3>
                  <FiNavigation
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Routes
                </h3>
                <div className="routes-list">
                  {Object.values(selectedTransporter.routes).map(
                    (route, index) => (
                      <div key={index} className="route-detail-bar">
                        <div className="route-side">
                          <span className="route-label">FROM</span>
                          <span className="route-address">
                            {route?.from || "Not Available"}
                          </span>
                        </div>
                        <div className="route-arrow">→</div>
                        <div className="route-side">
                          <span className="route-label">TO</span>
                          <span className="route-address">
                            {route?.to || "Not Available"}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : null}

            {/* Trucks */}
            {selectedTransporter.trucks &&
            Object.keys(selectedTransporter.trucks).length > 0 ? (
              <div className="details-section">
                <h3>
                  <FiTruck
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Trucks
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {Object.values(selectedTransporter.trucks).map(
                    (truck, index) => (
                      <div
                        key={index}
                        className="truck-card"
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          padding: "12px",
                        }}
                      >
                        {truck?.truckNumber ? (
                          <div className="truck-card-row">
                            <strong>
                              <FiTruck
                                style={{
                                  marginRight: "6px",
                                  verticalAlign: "middle",
                                }}
                              />
                              Truck Number:
                            </strong>{" "}
                            {truck.truckNumber}
                          </div>
                        ) : null}
                        {truck?.type ? (
                          <div className="truck-card-row">
                            <strong>
                              <FiTag
                                style={{
                                  marginRight: "6px",
                                  verticalAlign: "middle",
                                }}
                              />
                              Type:
                            </strong>{" "}
                            {truck.type}
                          </div>
                        ) : null}
                        {truck?.primaryNumber ? (
                          <div className="truck-card-row">
                            <strong>
                              <FiHash
                                style={{
                                  marginRight: "6px",
                                  verticalAlign: "middle",
                                }}
                              />
                              Primary Number:
                            </strong>{" "}
                            {truck.primaryNumber}
                          </div>
                        ) : null}
                        {truck?.secondaryNumber ? (
                          <div className="truck-card-row">
                            <strong>
                              <FiHash
                                style={{
                                  marginRight: "6px",
                                  verticalAlign: "middle",
                                }}
                              />
                              Secondary Number:
                            </strong>{" "}
                            {truck.secondaryNumber}
                          </div>
                        ) : null}
                        {truck?.from || truck?.to ? (
                          <div
                            className="truck-card-route"
                            style={{ marginTop: "8px" }}
                          >
                            <strong>
                              <FiNavigation
                                style={{
                                  marginRight: "6px",
                                  verticalAlign: "middle",
                                }}
                              />
                              Route
                            </strong>
                            <div>
                              {truck?.from ? (
                                <div>
                                  <FiMapPin
                                    style={{
                                      marginRight: "4px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  From: {truck.from}
                                </div>
                              ) : null}
                              {truck?.to ? (
                                <div>
                                  <FiMapPin
                                    style={{
                                      marginRight: "4px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  To: {truck.to}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : null}

            {/* Dispatches */}
            {selectedTransporter.dispatch &&
            Object.keys(selectedTransporter.dispatch).length > 0 ? (
              <div className="details-section">
                <h3>
                  <FiTruck
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  Dispatches
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table className="transport-orders-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {Object.keys(
                          Object.values(selectedTransporter.dispatch)[0] || {},
                        ).map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(selectedTransporter.dispatch).map(
                        (dispatch, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            {Object.keys(dispatch).map((key) => (
                              <td key={key}>
                                {typeof dispatch[key] === "object"
                                  ? JSON.stringify(dispatch[key])
                                  : String(dispatch[key] ?? "-")}
                              </td>
                            ))}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="empty-transporter-state">
            Select a transporter to view details.
          </div>
        )}
      </div>
      {showAssignConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "12px",
              minWidth: "350px",
              maxWidth: "90%",
            }}
          >
            <h3>Confirm Assignment</h3>
            <p>
              Are you sure you want to assign this client to
              <strong>
                {" "}
                {selectedTransporter?.displayName ||
                  selectedTransporter?.company}
              </strong>
              ?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button
                className="bg-secondary"
                onClick={() => setShowAssignConfirm(false)}
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  setShowAssignConfirm(false);
                  await assignClient();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transporters;
